import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { CreditCard, TrendingUp, UserPlus, Mail, AlertTriangle, ExternalLink, Hourglass } from "lucide-react";

const TIER_LABEL = { starter: "Starter", support: "Support", enterprise: "Enterprise" };
const gbp = (n) => `£${(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—");

export default function AdminSubscribersGlance({ stats, onViewAll }) {
  if (!stats) return null;
  const tiers = Object.entries(stats.tier_counts || {}).filter(([k]) => k !== "none");
  const t = stats.trials || {};
  const metrics = [
    { icon: CreditCard, label: "Paying subscribers", value: stats.active_subscribers, sub: tiers.length ? tiers.map(([k, v]) => `${v} ${TIER_LABEL[k] || k}`).join(" · ") : "No active plans yet" },
    { icon: TrendingUp, label: "Revenue this month", value: gbp(stats.revenue_this_month), sub: `${stats.orders_this_month || 0} payment${stats.orders_this_month === 1 ? "" : "s"} · ${gbp(stats.total_revenue)} all time` },
    { icon: Hourglass, label: "Trial conversion", value: t.conversion_rate == null ? "—" : `${t.conversion_rate}%`,
      sub: t.started ? `${t.started} started · ${t.converted} paid · ${t.cancelled} cancelled · ${t.in_trial} in trial` : "No trials started yet" },
    { icon: UserPlus, label: "New accounts (7 days)", value: stats.new_users_7d, sub: `${stats.total_users} accounts in total` },
    { icon: Mail, label: "Newsletter subscribers", value: stats.newsletter_subscribers, sub: `${gbp(stats.one_off_revenue)} from one-off purchases` },
  ];
  return (
    <section className="bg-card border border-border rounded-2xl shadow-sm p-6 mb-8" data-testid="admin-subscribers-glance">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight">Subscribers at a glance</h2>
          <p className="text-sm text-muted-foreground">Who is paying, what came in this month and who just joined.</p>
        </div>
        <div className="flex items-center gap-2">
          {stats.past_due > 0 && (
            <Badge variant="destructive" className="gap-1" data-testid="admin-past-due-badge"><AlertTriangle className="w-3 h-3" /> {stats.past_due} payment{stats.past_due === 1 ? "" : "s"} overdue</Badge>
          )}
          <a href="https://dashboard.stripe.com/subscriptions" target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="admin-stripe-link">
            Open Stripe <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
        {metrics.map((m) => (
          <div key={m.label} className="rounded-xl bg-secondary/40 border border-border p-4" data-testid={`glance-${m.label.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2"><m.icon className="w-4 h-4 text-primary" /> {m.label}</div>
            <p className="font-display text-2xl font-extrabold">{m.value}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{m.sub}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold">Newest sign-ups</h3>
        <button type="button" onClick={onViewAll} className="text-xs text-primary hover:underline" data-testid="glance-view-all">View all subscribers</button>
      </div>
      <ul className="divide-y divide-border" data-testid="glance-recent-signups">
        {(stats.recent_signups || []).map((u) => (
          <li key={u.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
            <div className="min-w-0">
              <p className="font-medium truncate">{u.name || u.email}</p>
              <p className="text-xs text-muted-foreground truncate">{u.org_name ? `${u.org_name} · ` : ""}{u.email}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Badge variant={u.status === "active" ? "default" : "secondary"}>{u.status === "active" ? TIER_LABEL[u.tier] || u.tier : "Free"}</Badge>
              <span className="text-xs text-muted-foreground w-14 text-right">{fmtDate(u.created_at)}</span>
            </div>
          </li>
        ))}
        {!stats.recent_signups?.length && <li className="py-6 text-center text-sm text-muted-foreground">No sign-ups yet. <Link to="/pricing" className="underline">Share your plans</Link>.</li>}
      </ul>
    </section>
  );
}
